import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Search,
    Filter,
    X,
    ChevronDown,
    Calendar,
    Copy,
    FileSpreadsheet,
    RotateCcw,
    Check,
    Archive,
    Building2,
    Briefcase,
    Users,
    UserRound,
    Tag
} from 'lucide-react';
import CircularProgress from '@mui/material/CircularProgress';
import api from '../../../api/client';
import PremiumSelect from '../component/PremiumSelect';
import { getTagColor } from '../../../utils/tagColors';

const PRESET_TAGS = ['Priority', 'Re-interview', 'Campus', 'Referral'];

const InterviewFilters = ({
    filters,
    statusCounts,
    candidates = [],
    onFilterChange,
    onClearFilters,
    hasActiveFilters,
    showAdvancedFilters,
    setShowAdvancedFilters,
    companies = [],
    departments = [],
    companyAdmins = [],
    jobDescriptions = [],
    statusOptions = [],
    onExport,
    onExportCSV,
    isExporting = false,
    totalCount = 0,
    selectedCount = 0
}) => {
    const { t } = useTranslation();
    const searchInputRef = useRef(null);
    const drawerRef = useRef(null);

    const [availableTags, setAvailableTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const [copiedCSV, setCopiedCSV] = useState(false);

    // Today's local date string (YYYY-MM-DD)
    const todayStr = useMemo(() => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }, []);

    // Fetch available tags from backend
    const fetchAvailableTags = useCallback(async () => {
        try {
            setTagsLoading(true);
            const response = await api.get('tags/', {
                headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` }
            });
            const tagsList = response.data?.results || response.data || [];
            setAvailableTags(tagsList);
        } catch (err) {
            console.error('Error fetching available tags:', err);
        } finally {
            setTagsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAvailableTags();
    }, [fetchAvailableTags]);

    // Sync selected tags from filters string
    useEffect(() => {
        if (filters.tags) {
            const tagsArray = filters.tags.split(',').map(t => t.trim()).filter(Boolean);
            setSelectedTags(tagsArray);
        } else {
            setSelectedTags([]);
        }
    }, [filters.tags]);

    // Keyboard shortcut: '/' focuses the search input
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && showAdvancedFilters) {
                setShowAdvancedFilters(false);
                return;
            }
            if (e.key === '/' && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showAdvancedFilters, setShowAdvancedFilters]);

    // Lock body scroll when drawer is open
    useEffect(() => {
        if (showAdvancedFilters) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [showAdvancedFilters]);

    const handleTagToggle = (tagName) => {
        let newTags;
        if (selectedTags.includes(tagName)) {
            newTags = selectedTags.filter(t => t !== tagName);
        } else {
            newTags = [...selectedTags, tagName];
        }
        setSelectedTags(newTags);
        onFilterChange('tags', newTags.join(','));
    };

    const handleCopyCSVClick = () => {
        if (onExportCSV) {
            onExportCSV();
            setCopiedCSV(true);
            setTimeout(() => setCopiedCSV(false), 2500);
        }
    };

    const handleClearAdvancedFilters = (e) => {
        e?.stopPropagation();
        if (typeof onFilterChange === 'function') {
            onFilterChange({
                company_id: '',
                department_id: '',
                scheduled_by: '',
                job_description_id: '',
                filter_date_from: '',
                filter_date_to: '',
                tags: '',
                show_archived: ''
            });
        }
        setSelectedTags([]);
    };

    // Merged tags: preset list + any extra from DB
    const allDisplayTags = useMemo(() => {
        const dbTags = availableTags.map(tag => (typeof tag === 'string' ? tag : tag.name)).filter(Boolean);
        return Array.from(new Set([...PRESET_TAGS, ...dbTags]));
    }, [availableTags]);

    // Count of active advanced filters (excluding search & status)
    const activeAdvancedFilterCount = useMemo(() => {
        return [
            filters.company_id,
            filters.department_id,
            filters.scheduled_by,
            filters.job_description_id,
            filters.filter_date_from,
            filters.filter_date_to,
            filters.tags,
            filters.show_archived === 'true'
        ].filter(Boolean).length;
    }, [filters]);

    // Status tabs definition matching Image 1
    const statusTabs = useMemo(() => {
        const allCount = statusCounts?.all ?? totalCount;
        const pendingCount = statusCounts?.pending ?? (candidates?.filter(c => c.status === 'pending').length || 0);
        const completedCount = statusCounts?.completed ?? (candidates?.filter(c => c.status === 'completed').length || 0);
        const expiredCount = statusCounts?.expired ?? (candidates?.filter(c => c.status === 'expired').length || 0);

        return [
            { value: '', label: 'All', count: allCount },
            { value: 'pending', label: 'Scheduled', count: pendingCount },
            { value: 'completed', label: 'Completed', count: completedCount },
            { value: 'expired', label: 'Expired', count: expiredCount },
        ];
    }, [statusCounts, totalCount, candidates]);

    // Dropdown options memoized for PremiumSelect
    const companyOptions = useMemo(() => [
        { value: '', label: t('interviewOverview.allCompanies', 'All companies') },
        ...companies.map((c) => ({
            value: String(c.id),
            label: c.name || String(c.id)
        }))
    ], [companies, t]);

    const departmentOptions = useMemo(() => [
        { value: '', label: t('interviewOverview.allDepartments', 'All departments') },
        ...departments.map((d) => ({
            value: String(d.id),
            label: d.name || String(d.id)
        }))
    ], [departments, t]);

    const companyAdminOptions = useMemo(() => [
        { value: '', label: t('interviewOverview.allCompanyAdmins', 'All company admins') },
        ...companyAdmins.map((admin) => ({
            value: String(admin.id),
            label: admin.name || String(admin.id)
        }))
    ], [companyAdmins, t]);

    const positionOptions = useMemo(() => [
        { value: '', label: t('interviewOverview.allPositions', 'All positions') },
        ...jobDescriptions.map((jd) => ({
            value: String(jd.id),
            label: jd.title || String(jd.id)
        }))
    ], [jobDescriptions, t]);

    return (
        <div className="bg-white border-b border-gray-100 px-4 sm:px-6 py-2.5">
            {/* Single Row: Search + Status Tabs + Copy CSV + Filters */}
            <div className="flex items-center justify-between gap-3 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden flex-nowrap">
                {/* Left side: Search input + Status Tabs */}
                <div className="flex items-center gap-2.5 sm:gap-3 flex-shrink-0 min-w-0">
                    {/* Search Bar */}
                    <div className="relative w-56 sm:w-64 lg:w-72 flex-shrink-0">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                            <Search size={15} />
                        </div>
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={filters.candidate_name || ''}
                            onChange={(e) => onFilterChange('candidate_name', e.target.value)}
                            placeholder="Search by candidate name, position..."
                            className="w-full pl-9 pr-10 py-1.5 text-xs sm:text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-800 placeholder-gray-400 shadow-2xs"
                        />
                        {/* Shortcut key indicator '/' or Clear button */}
                        <div className="absolute inset-y-0 right-0 pr-2 flex items-center gap-1">
                            {filters.candidate_name ? (
                                <button
                                    type="button"
                                    onClick={() => onFilterChange('candidate_name', '')}
                                    className="p-1 text-gray-400 hover:text-gray-600 rounded cursor-pointer"
                                    title="Clear search"
                                >
                                    <X size={13} />
                                </button>
                            ) : (
                                <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-[11px] font-mono text-gray-400 bg-gray-50 border border-gray-200 rounded">
                                    /
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Single Capsule for Status Tabs */}
                    <div className="inline-flex items-center p-1 bg-gray-100 border border-gray-200 rounded-xl flex-nowrap flex-shrink-0 gap-0.5">
                        {statusTabs.map((tab) => {
                            const isActive = (filters.status || '') === tab.value;
                            return (
                                <button
                                    key={tab.value || 'all'}
                                    type="button"
                                    onClick={() => onFilterChange('status', tab.value)}
                                    className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-150 select-none whitespace-nowrap cursor-pointer ${
                                        isActive
                                            ? 'bg-blue-600 text-white shadow-sm font-semibold'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/70'
                                    }`}
                                >
                                    <span>{tab.label}</span>
                                    <span
                                        className={`text-[11px] sm:text-xs px-1.5 py-0.5 rounded-md font-semibold transition-colors ${
                                            isActive
                                                ? 'bg-blue-700/60 text-white'
                                                : 'bg-white text-gray-700 border border-gray-200 shadow-2xs'
                                        }`}
                                    >
                                        {tab.count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Right side: Copy CSV & Filters Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                    <button
                        type="button"
                        onClick={handleCopyCSVClick}
                        disabled={isExporting || totalCount === 0}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                        title={selectedCount > 0 ? `Copy ${selectedCount} selected to CSV` : 'Copy CSV'}
                    >
                        {isExporting ? (
                            <CircularProgress size={13} sx={{ color: '#2563eb' }} />
                        ) : copiedCSV ? (
                            <Check size={14} className="text-emerald-600" />
                        ) : (
                            <Copy size={14} className="text-gray-500" />
                        )}
                        <span>{copiedCSV ? t('interviewOverview.copied', 'Copied!') : t('interviewOverview.copyCSV', 'Copy CSV')}</span>
                    </button>

                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            onClick={() => setShowAdvancedFilters(true)}
                            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-medium border transition-colors shadow-2xs whitespace-nowrap cursor-pointer ${
                                activeAdvancedFilterCount > 0 || showAdvancedFilters
                                    ? 'bg-blue-50 text-blue-700 border-blue-300'
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                            }`}
                        >
                            <Filter size={14} className={activeAdvancedFilterCount > 0 ? 'text-blue-600' : 'text-gray-500'} />
                            <span>{t('interviewOverview.filters', 'Filters')}</span>
                            {activeAdvancedFilterCount > 0 && (
                                <span className="ml-0.5 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-600 rounded-full">
                                    {activeAdvancedFilterCount}
                                </span>
                            )}
                        </button>

                        {activeAdvancedFilterCount > 0 && (
                            <button
                                type="button"
                                onClick={handleClearAdvancedFilters}
                                className="inline-flex items-center justify-center w-8 h-8 text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors cursor-pointer shadow-2xs"
                                title={t('interviewOverview.clearFilters', 'Clear advanced filters')}
                                aria-label="Clear advanced filters"
                            >
                                <X size={15} className="stroke-[2.5]" />
                            </button>
                        )}
                    </div>

                    {selectedCount > 0 && (
                        <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-md border border-blue-200 whitespace-nowrap">
                            {selectedCount} selected
                        </span>
                    )}
                </div>
            </div>

            {/* Advanced Filters Slide-Over Popup / Drawer matching Image 2 */}
            {showAdvancedFilters && (
                <div className="fixed inset-0 overflow-hidden" style={{ zIndex: 1300 }}>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/40 backdrop-blur-[1px] transition-opacity duration-200"
                        onClick={() => setShowAdvancedFilters(false)}
                    />

                    {/* Drawer Panel */}
                    <div
                        ref={drawerRef}
                        className="fixed inset-y-0 right-0 w-full sm:w-[440px] max-w-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200 ease-out"
                        style={{ zIndex: 1301 }}
                    >
                        {/* Drawer Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
                            <div className="flex items-center gap-2">
                                <Filter size={18} className="text-blue-600" />
                                <h2 className="text-lg font-bold text-gray-900 tracking-tight">
                                    {t('interviewOverview.advancedFilters', 'Advanced Filters')}
                                </h2>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowAdvancedFilters(false)}
                                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                                title="Close"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Drawer Body - Scrollable Fields */}
                        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
                            {/* 1. Company Filter */}
                            <PremiumSelect
                                label={t('interviewOverview.company', 'Company')}
                                icon={Building2}
                                iconColor="text-blue-600"
                                value={filters.company_id || ''}
                                onChange={(val) => onFilterChange('company_id', val)}
                                options={companyOptions}
                                placeholder={t('interviewOverview.allCompanies', 'All companies')}
                                className="w-full"
                                menuWidth="w-full"
                            />

                            {/* 2. Department Filter */}
                            <PremiumSelect
                                label={t('interviewOverview.department', 'Department')}
                                icon={Users}
                                iconColor="text-indigo-600"
                                value={filters.department_id || ''}
                                onChange={(val) => onFilterChange('department_id', val)}
                                options={departmentOptions}
                                placeholder={t('interviewOverview.allDepartments', 'All departments')}
                                className="w-full"
                                menuWidth="w-full"
                            />

                            {/* 3. Company Admin Filter */}
                            <PremiumSelect
                                label={t('interviewOverview.companyAdmin', 'Company admin')}
                                icon={UserRound}
                                iconColor="text-violet-600"
                                value={filters.scheduled_by || ''}
                                onChange={(val) => onFilterChange('scheduled_by', val)}
                                options={companyAdminOptions}
                                placeholder={t('interviewOverview.allCompanyAdmins', 'All company admins')}
                                className="w-full"
                                menuWidth="w-full"
                            />

                            {/* 4. Position Filter */}
                            <PremiumSelect
                                label={t('interviewOverview.position', 'Position')}
                                icon={Briefcase}
                                iconColor="text-amber-600"
                                value={filters.job_description_id || ''}
                                onChange={(val) => onFilterChange('job_description_id', val)}
                                options={positionOptions}
                                placeholder={t('interviewOverview.allPositions', 'All positions')}
                                className="w-full"
                                menuWidth="w-full"
                            />

                            {/* 5. Interview Date Range */}
                            <div>
                                <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-1.5 select-none">
                                    <Calendar size={13} className="text-blue-600" />
                                    <span>{t('interviewOverview.interviewDateRange', 'Interview date range')}</span>
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="relative">
                                        <input
                                            type="date"
                                            value={filters.filter_date_from || ''}
                                            max={filters.filter_date_to && filters.filter_date_to < todayStr ? filters.filter_date_to : todayStr}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val && val > todayStr) {
                                                    onFilterChange('filter_date_from', todayStr);
                                                } else {
                                                    onFilterChange('filter_date_from', val);
                                                }
                                            }}
                                            className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer shadow-xs"
                                            placeholder="dd-mm-yyyy"
                                        />
                                    </div>
                                    <div className="relative">
                                        <input
                                            type="date"
                                            value={filters.filter_date_to || ''}
                                            min={filters.filter_date_from || ''}
                                            max={todayStr}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val && val > todayStr) {
                                                    onFilterChange('filter_date_to', todayStr);
                                                } else {
                                                    onFilterChange('filter_date_to', val);
                                                }
                                            }}
                                            className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer shadow-xs"
                                            placeholder="dd-mm-yyyy"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 6. Tags Filter with colorful chips */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 select-none">
                                        <Tag size={13} className="text-violet-600" />
                                        <span>{t('interviewOverview.tags', 'Tags')}</span>
                                    </label>
                                    {tagsLoading && (
                                        <span className="text-[11px] text-gray-400">Loading tags...</span>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {allDisplayTags.map((tag) => {
                                        const isSelected = selectedTags.includes(tag);
                                        const color = getTagColor(tag);
                                        return (
                                            <button
                                                key={tag}
                                                type="button"
                                                onClick={() => handleTagToggle(tag)}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-150 border cursor-pointer select-none ${
                                                    isSelected
                                                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm ring-2 ring-blue-400/25'
                                                        : `${color.bg} ${color.text} ${color.border} ${color.hover} hover:shadow-2xs`
                                                }`}
                                            >
                                                {isSelected ? (
                                                    <Check size={12} className="stroke-[2.5]" />
                                                ) : (
                                                    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                                                )}
                                                <span>{tag}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* 7. Show archived toggle */}
                            <div className="pt-2 border-t border-slate-100">
                                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={filters.show_archived === 'true'}
                                        onChange={(e) => onFilterChange('show_archived', e.target.checked ? 'true' : '')}
                                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer"
                                    />
                                    <span className="text-xs font-semibold text-slate-700">
                                        {t('interviewOverview.showArchived', 'Show archived interviews')}
                                    </span>
                                </label>
                            </div>
                        </div>

                        {/* Drawer Footer matching Image 2 */}
                        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
                            <button
                                type="button"
                                onClick={() => {
                                    onClearFilters();
                                    setSelectedTags([]);
                                }}
                                className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t('interviewOverview.reset', 'Reset')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowAdvancedFilters(false)}
                                className="px-6 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-colors"
                            >
                                {t('interviewOverview.showResults', {
                                    count: totalCount,
                                    defaultValue: `Show ${totalCount} results`
                                })}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(InterviewFilters);